import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import styles from '../CheckListSetting/ChecklistSetting.module.scss';

interface TodoCategoryFormProps {
    mode: 'create' | 'edit';
    initialLabel?: string;
    onBack: () => void;
    onSave: (label: string) => Promise<void>;
    canSave?: boolean;
}

const TodoCategoryForm: React.FC<TodoCategoryFormProps> = ({
    mode,
    initialLabel = '',
    onBack,
    onSave,
    canSave = false,
}) => {
    const [label, setLabel] = useState(initialLabel);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        setLabel(initialLabel);
    }, [initialLabel]);

    const trimmed = label.trim();
    const isAtLimit = label.length >= 200;
    const unchanged = mode === 'edit' && trimmed === initialLabel.trim();
    const disableSave = !canSave || !trimmed || unchanged || saving;

    const handleSave = async () => {
        if (disableSave) return;

        setSaving(true);
        setSaveError(null);
        try {
            await onSave(trimmed);
        } catch (error: any) {
            setSaveError(error?.error || error?.message || 'Failed to save category');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Paper elevation={3} className={styles.containerr} style={{ padding: '20px', backgroundColor: 'whitesmoke' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button variant="outlined" onClick={onBack} className={styles.button}>
                    Back
                </Button>
                <Button
                    style={{
                        backgroundColor: disableSave ? 'rgba(68, 99, 136, 0.5)' : 'rgb(68, 99, 136)',
                        color: 'white',
                        padding: '5px 15px',
                        textTransform: 'capitalize',
                    }}
                    onClick={handleSave}
                    disabled={disableSave}
                    title={canSave ? 'Save category' : 'You do not have permission to save'}
                >
                    {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
                </Button>
            </Box>

            {saveError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {saveError}
                </Alert>
            )}

            <div className={styles.rightPanel}>
                <Typography variant="h6" mb={2}>
                    {mode === 'edit' ? 'Edit Todo Category' : 'New Todo Category'}
                </Typography>

                <TextField
                    label="Category Name"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    fullWidth
                    required
                    size="small"
                    inputProps={{ maxLength: 200 }}
                    helperText={isAtLimit ? 'Maximum 200 characters allowed' : ''}
                    disabled={!canSave}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleSave();
                        }
                    }}
                />
            </div>
        </Paper>
    );
};

export default TodoCategoryForm;
